"use client";

import { useState } from "react";
import Link from "next/link";

type BillingPeriod = "monthly" | "yearly";

const STARTER_FEATURES = [
  "Drag & drop builder",
  "Embedded forms",
  "Secure storage",
  "Email notifications",
];

const PROFESSIONAL_FEATURES = [
  "Everything in Starter",
  "WordPress plugin",
  "Joomla plugin",
  "API access",
  "Conditional logic",
  "Audit logs",
];

const ENTERPRISE_FEATURES = [
  "Everything in Professional",
  "Dedicated infrastructure",
  "Advanced API limits",
  "Custom integrations",
  "Priority support",
  "BAA included",
];

export function PricingTiers() {
  const [period, setPeriod] = useState<BillingPeriod>("monthly");

  const monthlyPrices = { starter: 49, professional: 149, enterprise: null };
  const yearlyPrices = { starter: 39, professional: 119, enterprise: null };

  const starterPrice = period === "monthly" ? monthlyPrices.starter : yearlyPrices.starter;
  const proPrice = period === "monthly" ? monthlyPrices.professional : yearlyPrices.professional;

  return (
    <div className="mt-8">
      <div className="mb-10 flex justify-center">
        <div className="inline-flex rounded-full bg-white/40 p-1 ring-1 ring-white/40 backdrop-blur-xl dark:bg-white/10 dark:ring-white/20">
          <button
            type="button"
            onClick={() => setPeriod("monthly")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              period === "monthly"
                ? "bg-[#27598e]/90 text-white dark:bg-white dark:text-black"
                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setPeriod("yearly")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              period === "yearly"
                ? "bg-[#27598e]/90 text-white dark:bg-white dark:text-black"
                : "text-slate-600 hover:text-slate-900 dark:text-white/70 dark:hover:text-white"
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Starter */}
        <div className="rounded-[2rem] bg-white/40 p-6 ring-1 ring-white/30 backdrop-blur-2xl dark:bg-white/10 dark:ring-white/20">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Starter</h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-white/70">Small clinics</p>
          <div className="mt-4">
            <span className="text-3xl font-bold text-slate-900 dark:text-white">${starterPrice}</span>
            <span className="text-slate-600 dark:text-white/70">/mo</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-slate-600 dark:text-white/70">
            {STARTER_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                {f}
              </li>
            ))}
          </ul>
          <Link
            href="/apps/hipaa-compliant-forms/register"
            className="mt-6 block w-full rounded-2xl bg-white/50 py-3 text-center text-sm font-medium text-slate-700 ring-1 ring-white/40 backdrop-blur-xl transition-colors hover:bg-white/60 dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/15"
          >
            Get started
          </Link>
        </div>

        {/* Professional */}
        <div className="rounded-[2rem] bg-white/40 p-6 ring-1 ring-white/30 backdrop-blur-2xl dark:bg-white/10 dark:ring-white/20 md:ring-2 md:ring-cyan-500/50">
          <div className="inline-block rounded-full bg-cyan-500/20 px-3 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-300">
            Popular
          </div>
          <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">Professional</h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-white/70">Growing practices</p>
          <div className="mt-4">
            <span className="text-3xl font-bold text-slate-900 dark:text-white">${proPrice}</span>
            <span className="text-slate-600 dark:text-white/70">/mo</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-slate-600 dark:text-white/70">
            {PROFESSIONAL_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                {f}
              </li>
            ))}
          </ul>
          <Link
            href="/apps/hipaa-compliant-forms/register"
            className="mt-6 block w-full rounded-2xl bg-[#27598e]/90 py-3 text-center text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-[#27598e] dark:bg-white dark:text-black dark:hover:bg-white/90"
          >
            Get started
          </Link>
        </div>

        {/* Enterprise */}
        <div className="rounded-[2rem] bg-white/40 p-6 ring-1 ring-white/30 backdrop-blur-2xl dark:bg-white/10 dark:ring-white/20">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Enterprise</h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-white/70">Large organizations</p>
          <div className="mt-4">
            <span className="text-3xl font-bold text-slate-900 dark:text-white">Custom</span>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-slate-600 dark:text-white/70">
            {ENTERPRISE_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                {f}
              </li>
            ))}
          </ul>
          <Link
            href="/contact"
            className="mt-6 block w-full rounded-2xl bg-white/50 py-3 text-center text-sm font-medium text-slate-700 ring-1 ring-white/40 backdrop-blur-xl transition-colors hover:bg-white/60 dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/15"
          >
            Contact Sales
          </Link>
        </div>
      </div>
    </div>
  );
}
